// import Geocode from 'react-geocode';
import { CompanyMember, GroupUser, GroupUserStatus } from "../graphql/types";
import { getDateFnsLocale } from "../locales/dateFnsLocale";
import logger from "./logs/LoggerInstance";

const mailformat =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const removeEmptySpace = (text: string) => text.replace(/\s/g, "");

export const isValidEmail = (text: string) =>
  !text ? false : text.match(mailformat);

export const stringToEmailList = (users: string): string[] =>
  users
    ? users
        .trim()
        .split(/[,;\s]+/)
        .filter((el) => isValidEmail(el))
    : [];

export const renderTimer = (timer) => {
  const hours = Math.floor(timer / 3600);
  const minutes = Math.floor((timer % 3600) / 60);
  const seconds = Math.floor((timer % 3600) % 60);
  const hourString = hours > 0 ? `${`${hours}`.padStart(2, "0")}:` : "";
  const minuteString = `${`${minutes}`.padStart(2, "0")}:`;
  const secondsString = `${seconds}`.padStart(2, "0");
  return `${hourString}${minuteString}${secondsString}`;
};

export const renderInitials = (name: string = "N / A") => {
  // get if the name is null, not only undefined
  let initial = ""; // not available
  if (name) {
    initial = name
      .split(/\s/)
      .map((word) => (word[0] || "").toUpperCase())
      .slice(0, 3)
      .join("")
      .trim();
  }
  return initial;
};

export const filterArrayByString = (values, filteredBy) =>
  values.filter((name) => name.match(new RegExp(filteredBy, "i")));

export const filterArrayOfObjectsByString = <
  P extends string,
  T extends { [key: string]: any },
>(
  values: T[],
  property: P,
  filteredBy: string,
): T[] =>
  values.filter((name) => name[property]?.match(new RegExp(filteredBy, "i")));

export const filterArrayByObjPropertyValue = (
  values,
  obj,
  index,
  propOfObj,
  filteredBy,
) =>
  values.filter((name) =>
    name[obj][index][propOfObj]?.match(new RegExp(filteredBy, "i")),
  );

const getDateOptions = (): Pick<
  Intl.DateTimeFormatOptions,
  "day" | "month" | "year"
> => ({
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const getTimeOptions = (): Pick<
  Intl.DateTimeFormatOptions,
  "hour" | "minute"
> => ({
  hour: "numeric",
  minute: "numeric",
});

export const renderDate = (
  value: Date,
  { onlyDate = true, onlyTime = false },
) => {
  let options: Intl.DateTimeFormatOptions = {};
  if (onlyDate) {
    options = { ...options, ...getDateOptions() };
  }
  if (onlyTime) {
    options = { ...options, ...getTimeOptions() };
  }
  return value.toLocaleString(undefined, options);
};

export const sortArrayOfObjectByKey = <T>(list: T[], key: keyof T): T[] =>
  [...list].sort((a, b) => {
    if (a[key] < b[key]) {
      return -1;
    }
    if (a[key] > b[key]) {
      return 1;
    }
    return 0;
  });

export const isDuplicateDocument = (
  newDocument: { id: string },
  existingDocuments: { id: string }[],
) => {
  if (existingDocuments && existingDocuments.length > 0) {
    return (
      newDocument.id !== null &&
      existingDocuments.some((doc) => newDocument.id === doc.id)
    );
  }

  return false;
};

export const cloneList = <T>(list: T[]): T[] => [...list];

export const cleanName = (name) => {
  return name
    ? name
        .replace(/undefined/g, "")
        .replace(/null/g, "")
        .trim()
    : null;
};

export const getWebAppVersion = async () => {
  const requestPath = "/infoVersion.json";
  try {
    const response = await fetch(requestPath);

    if (!response.ok) {
      logger.error("Error on getWebAppVersion", ["getWebappVersion"], {
        requestPath,
        originalError: {
          response: {
            status: response.status,
            statusText: response.statusText,
          },
        },
      });
      return null;
    }

    const data = await response.json();
    return data.version;
  } catch (err) {
    logger.error("Error on getWebAppVersion", ["getWebappVersion"], {
      requestPath,
      originalError: {
        name: err instanceof Error ? err.name : "UnknownError",
        message: err instanceof Error ? err.message : String(err),
      },
    });
    return null;
  }
};

export const sortByFirstLastNameAndEmail = (
  array,
  property,
): GroupUser[] | CompanyMember[] => {
  return [...array].sort((a, b) => {
    let aIsAdmin = false;
    let bIsAdmin = false;
    if (property === "status") {
      aIsAdmin = a[property] === GroupUserStatus.Admin;
      bIsAdmin = b[property] === GroupUserStatus.Admin;
    } else if (property === "roles") {
      aIsAdmin = a[property].includes("ADMIN");
      bIsAdmin = b[property].includes("ADMIN");
    }
    const nameA = cleanName(
      "".concat(a.user.firstName).concat(a.user.lastName),
    );
    const nameB = cleanName(
      "".concat(b.user.firstName).concat(b.user.lastName),
    );
    if (aIsAdmin !== bIsAdmin) {
      return aIsAdmin ? -1 : 1;
    }
    return (
      (nameA &&
        nameB &&
        nameA.toLowerCase().localeCompare(nameB.toLowerCase())) ||
      (a.user.email &&
        b.user.email &&
        a.user.email.toLowerCase().localeCompare(b.user.email.toLowerCase()))
    );
  });
};

export const formatNumber = (val: number) => {
  if (val === null || val === undefined || isNaN(val)) {
    return "-";
  }
  let sign = 1;
  if (val < 0) {
    sign = -1;
    val = -val;
  }
  // trim the number decimal point if it exists
  const num = val.toString().includes(".")
    ? val.toString().split(".")[0]
    : val.toString();
  const len = num.toString().length;
  let result = "";
  let count = 1;

  for (let i = len - 1; i >= 0; i--) {
    result = num.toString()[i] + result;
    if (count % 3 === 0 && count !== 0 && i !== 0) {
      result = " " + result;
    }
    count++;
  }

  // add number after decimal point
  if (val.toString().includes(".")) {
    result = result + "." + val.toString().split(".")[1].slice(0, 2);
  }

  if (
    val.toString().split(".")[1] &&
    val.toString().split(".")[1].length === 1
  ) {
    result = result + "0";
  }

  result = result.replace(".", ",");
  // return result with - sign if negative
  return sign < 0 ? "-" + result : result;
};

export const getLocalizationAdapterLocale = (lang: string) => {
  return getDateFnsLocale(lang);
};

type FlattenedObject = { [key: string]: string | number | boolean | null };

export const flattenObject = (obj: object, parentKey = "", separator = ".") => {
  if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
    return {};
  }
  return Object.entries(obj).reduce((acc: FlattenedObject, [key, value]) => {
    const newKey = parentKey ? `${parentKey}${separator}${key}` : key;

    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      Object.assign(acc, flattenObject(value as object, newKey, separator));
    } else {
      acc[newKey] = value;
    }

    return acc;
  }, {});
};
